import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Divider,
  List,
  ListItem,
  ListIcon,
  Badge,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaIndustry,
  FaFlask,
  FaDollarSign,
  FaCube,
  FaClock,
  FaMicrochip,
  FaCog,
  FaSync,
  FaChartLine,
  FaWater,
  FaTint,
  FaBolt,
} from 'react-icons/fa';
import Footer from '../Footer';

const GreshoVortexValidation = () => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.800');
  const [showAnimation, setShowAnimation] = useState(false);

  return (
    <Box minH="100vh" bg="gray.50">
      <Helmet>
        <title>Gresho Vortex Benchmark: Accurate Vortex Dynamics Validation | SRS Blog</title>
        <meta name="description" content="The Gresho vortex is a balance test — a rotating flow that must hold its shape with no external forcing. How SRS validates long-time vortex preservation." />
        <link rel="canonical" href="https://srs.numericalai.net/blog/gresho-vortex-validation" />
        <meta property="og:title" content="Gresho Vortex Benchmark: Accurate Vortex Dynamics Validation | SRS Blog" />
        <meta property="og:description" content="The Gresho vortex is a balance test — a rotating flow that must hold its shape with no external forcing. How SRS validates long-time vortex preservation." />
        <meta property="og:url" content="https://srs.numericalai.net/blog/gresho-vortex-validation" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gresho Vortex Benchmark: Accurate Vortex Dynamics Validation | SRS Blog" />
        <meta name="twitter:description" content="The Gresho vortex is a balance test — a rotating flow that must hold its shape with no external forcing. How SRS validates long-time vortex preservation." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image 
                src="/simple-logo.png" 
                alt="SRS Logo" 
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <Button
              leftIcon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </HStack>
        </Container>
      </Box>

      <Container maxW="5xl" py={12}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center">
            <Badge colorScheme="green" fontSize="md" mb={4} px={3} py={1}>
              Validation Study
            </Badge>
            <Heading 
              as="h1"
              size="2xl" 
              color="brand.500" 
              mb={6}
              fontWeight="bold"
            >
              Gresho Vortex Benchmark
            </Heading>
            <Heading 
              size="lg" 
              color="gray.600" 
              mb={6}
              fontWeight="medium"
            >
              Accurate Vortex Dynamics for Industrial Flow Design
            </Heading>
            
            <Text 
              fontSize="xl" 
              color="gray.700" 
              lineHeight="tall"
              mb={6}
              textAlign="left"
            >
              Some systems don't explode or fail — they simply need to stay perfectly balanced over time. The Gresho Vortex is a classic test of that balance: a rotating flow that should keep its shape as it spins without any external forcing or dissipation.
            </Text>
            
            <Text 
              fontSize="lg" 
              color="gray.600" 
              lineHeight="tall"
              textAlign="left"
            >
              With NumericalAI, engineers can instantly run this type of benchmark in the cloud — proving solver accuracy, stability, and reliability without any setup, installation, or hardware concerns. If a CFD solver can maintain a perfect vortex structure over hundreds of rotations, it demonstrates the numerical precision needed for real industrial applications.
            </Text>
          </Box>

          {/* Animation Toggle */}
          <Box textAlign="center">
            <Button
              size="lg"
              colorScheme={showAnimation ? "gray" : "brand"}
              onClick={() => setShowAnimation(!showAnimation)}
              leftIcon={<FaSync />}
            >
              {showAnimation ? "Show Static Images" : "Show Animation"}
            </Button>
          </Box>

          {/* Images or Animation */}
          {showAnimation ? (
            <Box 
              bg={cardBg}
              borderRadius="xl"
              overflow="hidden"
              shadow="md"
              border="1px"
              borderColor={borderColor}
            >
              <Image 
                src={require('../../img/blogs/2D_GreshoVortex_pressure_animation_NumericalAI.gif')}
                alt="Gresho Vortex Pressure Animation"
                w="100%"
                objectFit="contain"
                bg="gray.100"
              />
              <Box p={6} bg={sectionBg}>
                <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2} textAlign="center">
                  <strong>Time Evolution Animation</strong>
                </Text>
                <Text fontSize="md" color="gray.600" lineHeight="tall" textAlign="center">
                  Pressure field evolution showing the vortex maintaining its structure over 700 seconds. Note the absence of numerical diffusion or artificial dissipation — a hallmark of high-quality CFD solvers.
                </Text>
              </Box>
            </Box>
          ) : (
            <Tabs variant="enclosed" colorScheme="brand">
              <TabList bg={sectionBg} borderRadius="xl" p={2}>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }}>
                  <Icon as={FaTint} mr={2} /> Initial Density
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }}>
                  <Icon as={FaTint} mr={2} /> Density at t=700s
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }}>
                  <Icon as={FaBolt} mr={2} /> Pressure
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }}>
                  <Icon as={FaWater} mr={2} /> Velocity
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="md"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/2D_GreshoVortex_denstiy_contour_NumericalAI.jpg')}
                      alt="Gresho Vortex Initial Density"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Initial Vortex Structure (t = 0)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        The initial condition shows a clean, symmetric density profile with a rotating core. The vortex is in exact equilibrium between centrifugal force and pressure gradient — any numerical error will cause degradation over time.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="md"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/2D_GreshoVortex_denstiy_contour_time_700s_NumericalAI.jpg')}
                      alt="Gresho Vortex Density at 700s"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Later in Time (t = 700s)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        After 700 seconds (approximately 100 full rotations), the structure remains intact. This demonstrates exceptional solver stability with negligible numerical dissipation — the vortex profile is virtually unchanged from its initial state.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/2D_GreshoVortex_pressure_contour_NumericalAI.jpg')}
                      alt="Gresho Vortex Pressure"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Pressure Field Distribution</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        The pressure field remains smooth throughout the simulation with no spurious oscillations. The radial pressure gradient perfectly balances the centrifugal acceleration, maintaining the vortex in steady rotation.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/2D_GreshoVortex_vel_contour_NumericalAI.jpg')}
                      alt="Gresho Vortex Velocity"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Velocity Field — Stable Rotating Core</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Velocity contours reveal the azimuthal (tangential) velocity profile that characterizes the Gresho vortex. The peak velocity occurs at mid-radius, smoothly transitioning to zero at the center and outer boundaries.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}

          <Divider />

          {/* Benchmark Overview */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              What is the Gresho Vortex?
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Text fontSize="lg" color="gray.700" lineHeight="tall" mb={4}>
                  The Gresho Vortex is a <strong>standard benchmark problem</strong> in computational fluid dynamics designed to test a solver's ability to maintain vortical structures without spurious numerical dissipation or dispersion. Originally proposed by Gresho and Chan (1990), it consists of:
                </Text>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Steady-state rotating flow</strong> with no external forcing
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Exact balance</strong> between pressure gradient and centrifugal force
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Sensitive to numerical errors</strong> — any artificial dissipation or pressure-velocity coupling issues will degrade the solution
                    </Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      <strong>Long-time integration test</strong> — the vortex should persist indefinitely without decay
                    </Text>
                  </ListItem>
                </List>
              </Box>

              <Box bg="blue.50" p={6} borderRadius="xl" border="2px" borderColor="blue.200">
                <Heading size="md" color="blue.800" mb={3}>
                  Why This Test Matters
                </Heading>
                <Text fontSize="md" color="blue.900" lineHeight="tall">
                  Many CFD solvers introduce artificial dissipation to maintain stability, but too much dissipation destroys important flow features like vortices, boundary layers, and shear layers. The Gresho Vortex benchmark proves that NumericalAI's solvers preserve these structures accurately — essential for industrial applications where flow features directly impact performance.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Simulation Configuration */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Simulation Configuration
            </Heading>
            
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={8}>
              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCube} boxSize={6} color="brand.500" />
                    <Heading size="md" color="gray.800">Domain & Grid</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Formulation:</Text>
                      <Text fontWeight="semibold">2D Cartesian</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Domain Size:</Text>
                      <Text fontWeight="semibold">1 m × 1 m</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Grid Resolution:</Text>
                      <Text fontWeight="semibold">400 × 400</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Total Cells:</Text>
                      <Text fontWeight="semibold">160,000</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Cell Size:</Text>
                      <Text fontWeight="semibold">2.5 mm uniform</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaSync} boxSize={6} color="purple.500" />
                    <Heading size="md" color="gray.800">Initial Conditions</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Vortex Radius:</Text>
                      <Text fontWeight="semibold">R₀ = 0.2 m</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Peak Velocity:</Text>
                      <Text fontWeight="semibold">~0.5 m/s</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Mach Number:</Text>
                      <Text fontWeight="semibold">M ≈ 0.0015</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Density:</Text>
                      <Text fontWeight="semibold">ρ = 1.0 kg/m³</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Ambient Pressure:</Text>
                      <Text fontWeight="semibold">101.325 kPa</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaClock} boxSize={6} color="orange.500" />
                    <Heading size="md" color="gray.800">Temporal Settings</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Simulation Time:</Text>
                      <Text fontWeight="semibold">700 seconds</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Rotations:</Text>
                      <Text fontWeight="semibold">~100 full rotations</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Time Step:</Text>
                      <Text fontWeight="semibold">Adaptive CFL</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">CFL Number:</Text>
                      <Text fontWeight="semibold">0.4</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Output Frequency:</Text>
                      <Text fontWeight="semibold">Every 10s</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCog} boxSize={6} color="blue.500" />
                    <Heading size="md" color="gray.800">Solver Configuration</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Spatial Scheme:</Text>
                      <Text fontWeight="semibold">WENO5</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Time Integration:</Text>
                      <Text fontWeight="semibold">RK3-TVD</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Riemann Solver:</Text>
                      <Text fontWeight="semibold">HLLC</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Equation of State:</Text>
                      <Text fontWeight="semibold">Ideal Gas</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Boundary:</Text>
                      <Text fontWeight="semibold">Periodic</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>

            <Box bg="green.50" p={6} borderRadius="xl" border="2px" borderColor="green.200">
              <HStack spacing={3} mb={3}>
                <Icon as={FaMicrochip} boxSize={6} color="green.600" />
                <Heading size="md" color="green.800">Computational Performance</Heading>
              </HStack>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">~6 hrs</Text>
                  <Text fontSize="sm" color="gray.600">Wall-Clock Time</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">A100</Text>
                  <Text fontSize="sm" color="gray.600">NVIDIA GPU</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">~750 MB</Text>
                  <Text fontSize="sm" color="gray.600">Memory Usage</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">~7×</Text>
                  <Text fontSize="sm" color="gray.600">Speedup vs CPU</Text>
                </Box>
              </SimpleGrid>
            </Box>
          </Box>

          <Divider />

          {/* Validation Metrics */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Validation Metrics & Accuracy
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Quantitative Performance Indicators
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={4}>
                  To assess solver quality, we track several conservation and accuracy metrics:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      Total Energy Conservation
                    </Text>
                    <Text color="gray.600" fontSize="sm" mb={3}>
                      Energy drift over 700s: &lt; 0.02%
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                      Indicates excellent time integration accuracy with minimal numerical dissipation.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      Angular Momentum
                    </Text>
                    <Text color="gray.600" fontSize="sm" mb={3}>
                      Conservation error: &lt; 0.01%
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                      Confirms that rotational symmetry is respected by the numerical scheme.
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      L² Density Error
                    </Text>
                    <Text color="gray.600" fontSize="sm" mb={3}>
                      Relative error at t=700s: ~0.15%
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                      Density profile remains nearly identical to initial condition — hallmark of low-dissipation schemes.
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      <Icon as={FaCheckCircle} color="green.500" mr={2} />
                      Peak Velocity Retention
                    </Text>
                    <Text color="gray.600" fontSize="sm" mb={3}>
                      Velocity decay: &lt; 0.3%
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                      The vortex maintains its strength without artificial damping effects.
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              <Box bg="purple.50" p={6} borderRadius="xl" border="2px" borderColor="purple.200">
                <Heading size="md" color="purple.800" mb={4}>
                  Comparison with Literature
                </Heading>
                <Text fontSize="md" color="purple.900" lineHeight="tall">
                  NumericalAI's results match or exceed published benchmarks from leading CFD research groups. The combination of <strong>WENO5 spatial reconstruction</strong> and <strong>RK3-TVD time integration</strong> delivers the accuracy expected from modern high-order methods — but now accessible through a simple cloud interface instead of months of code development.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Why Engineers Choose NumericalAI */}
          <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
            <Heading size="lg" color="brand.500" mb={6}>
              Why Engineers Choose NumericalAI
            </Heading>
            <List spacing={4}>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Cloud-native simulation</strong> — run advanced CFD workflows without managing servers, installing software, or configuring HPC clusters
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Proven accuracy</strong> — validated on industry-standard benchmarks like the Gresho Vortex, ensuring your results are trustworthy
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Faster insights</strong> — launch, monitor, and visualize your results directly in your browser with GPU acceleration
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Consistent results</strong> — reliable for long-duration, steady-state, and transient analysis across diverse applications
                </Text>
              </ListItem>
            </List>
          </Box>

          {/* Industrial Relevance */}
          <Box>
            <Heading size="lg" color="brand.500" mb={6} textAlign="center">
              Industrial Relevance
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaIndustry} boxSize={8} color="orange.500" />
                  <Heading size="md" color="gray.800">
                    Energy & Process Engineering
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Predict circulation patterns, flow mixing, and residence time distributions in reactors, tanks, and separators. Accurate vortex modeling ensures optimal mixing and prevents dead zones.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaCog} boxSize={8} color="blue.500" />
                  <Heading size="md" color="gray.800">
                    Aerospace & Turbomachinery
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Study tip vortices, secondary flows, and vortex breakdown in compressors, turbines, and propellers. Solver stability under rotation is critical for reliable performance predictions.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaSync} boxSize={8} color="purple.500" />
                  <Heading size="md" color="gray.800">
                    Manufacturing & Mixing
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Simulate mixers, impellers, stirred tanks, and cooling channels. Vortex preservation ensures accurate prediction of blend time, homogeneity, and energy consumption.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaFlask} boxSize={8} color="green.500" />
                  <Heading size="md" color="gray.800">
                    R&D Validation
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Demonstrate solver accuracy and numerical stability effortlessly. Use the Gresho Vortex as a quick sanity check before committing to expensive production runs.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Why It Matters */}
          <Box 
            bg="brand.500" 
            color="white" 
            p={8} 
            borderRadius="xl" 
            shadow="xl"
            position="relative"
            overflow="hidden"
          >
            <Box position="relative" zIndex={1}>
              <HStack spacing={3} mb={4}>
                <Icon as={FaChartLine} boxSize={8} />
                <Heading size="lg">Why It Matters</Heading>
              </HStack>
              <Text fontSize="lg" lineHeight="tall" mb={4}>
                NumericalAI transforms advanced flow simulation into a fast, collaborative, and cloud-powered experience. No installation barriers, no hardware procurement delays — just instant access to validated, production-grade CFD.
              </Text>
              <Text fontSize="lg" lineHeight="tall" fontWeight="semibold">
                A test like the Gresho Vortex demonstrates that if NumericalAI can maintain a perfect vortex structure over 100 rotations, it can handle your real-world designs — reliably, accurately, and efficiently, every time.
              </Text>
            </Box>
            
            {/* Decorative background */}
            <Box
              position="absolute"
              top="-50%"
              right="-10%"
              width="300px"
              height="300px"
              borderRadius="full"
              bg="whiteAlpha.100"
              zIndex={0}
            />
          </Box>

          {/* Call to Action */}
          <Box textAlign="center" py={8}>
            <VStack spacing={4}>
              <Heading size="md" color="gray.700">
                Ready to Validate Your Solver?
              </Heading>
              <Text color="gray.600" fontSize="lg" maxW="2xl">
                Run the Gresho Vortex benchmark yourself on NumericalAI's cloud platform. See the accuracy and stability in action.
              </Text>
              <HStack spacing={4} mt={4}>
                <Button
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/start-simulation')}
                >
                  Start Your Simulation
                </Button>
                <Button
                  variant="outline"
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

export default GreshoVortexValidation;